import React from "react";
import { useAtom, useSetAtom, useAtomValue } from "jotai";
import type { Stop } from "../types";
import type { Grid } from "./useStops";
import { useStops } from "./useStops";
import {
  selectedStopIdsAtom,
  addStopIdAtom,
  addStopIdsAtom,
  removeStopIdAtom,
  clearStopsAtom,
  selectedTpidAtom,
} from "../atoms";
import { activeSelectionAtom } from "../atoms/activeSelection";

export function useSelectedStops(grid?: Grid) {
  const tpidOverride = useAtomValue(selectedTpidAtom);

  const gridForStops = React.useMemo(
    () => (grid ? { ...grid } : grid),
    [grid, tpidOverride]
  );

  const { byId, stops: allStops } = useStops(gridForStops);

  const [stopIds, setStopIds] = useAtom(selectedStopIdsAtom);

  const [activeSelection, setActiveSelection] = useAtom(activeSelectionAtom);
  const activeStopId =
    activeSelection?.kind === "stop" ? activeSelection.id : null;

  const setActiveStopId = (id: string | null) => {
    if (!id) setActiveSelection(null);
    else setActiveSelection({ kind: "stop", id });
  };

  const addStopId = useSetAtom(addStopIdAtom);
  const addStopIds = useSetAtom(addStopIdsAtom);
  const removeStopId = useSetAtom(removeStopIdAtom);
  const clearStops = useSetAtom(clearStopsAtom);

  const selectedStops = React.useMemo<Stop[]>(
    () => stopIds.map((id) => byId.get(id)).filter(Boolean) as Stop[],
    [stopIds, byId]
  );

  const addStop = (s: Stop | string) => {
    const id = typeof s === "string" ? s : s.id;
    addStopId(id);
  };

  const addStops = (arr: (Stop | string)[]) => {
    const ids = arr.map((x) => (typeof x === "string" ? x : x.id));
    addStopIds(ids);
  };

  const removeStop = (id: string) => {
    removeStopId(id);
    if (activeStopId === id) setActiveSelection(null);
  };

  const clearStopsWrapped = () => {
    clearStops();
    if (activeSelection?.kind === "stop") setActiveSelection(null);
  };

  return {
    allStops,
    byId,
    stopIds,
    setStopIds,
    selectedStops,
    activeStopId,
    setActiveStopId,
    addStop,
    addStops,
    removeStop,
    clearStops: clearStopsWrapped,
  };
}
